// Arma la lista homologada de pasos (renglón .lx-paso + chip ESTADOS_LX) a
// partir de lo que manda POST /flow. Solo presentación: no decide nada que el
// backend no haya decidido ya.
//
// Mapeo con pulpi-v4-app-backend, app/controllers/flow.controller.js (getFlow).
// El backend agrupa cada paso visible (InputCatalog.showUser) según su FlowData:
//
//   grupo         FlowData del paso                         estado en la liga        ¿se abre?
//   ------------  ----------------------------------------  -----------------------  ---------------------
//   completed     VALIDATED                                 Listo                    no (igual que antes),
//                                                                                    salvo multiarchivo con
//                                                                                    rango («de 4 a 6») que
//                                                                                    pasó con el mínimo y
//                                                                                    aún tiene opcionales
//   inValidation  cualquier otro: MANUAL_VALIDATION,        En revisión              no (igual que antes)
//                 EXPIRED…
//   rejected      REJECTED (+ comment = motivo)             Corrige + motivo         sí, siempre
//   unfinished    IN_PROGRESS: lo empezó y falta algo        Te toca / Pendiente      sí, si no está disabled
//                 (validación con errores, multiarchivo
//                 incompleto, sesión de INCODE o firma)
//   inProcess     sin FlowData: no lo ha empezado            Te toca / Pendiente      sí, si no está disabled
//
//   disabled  = algún `order` de dependsOn aún no está VALIDATED → Bloqueado,
//               «Se habilita cuando termines X» (o «cuando revisemos X» si X
//               ya lo mandó y está en revisión).
//   expired   = el documento pasó sus daysToBeEffective; no cambia de grupo y la
//               lista no lo usaba: se sigue ignorando.
//   reutilizadoDe = el backend copió el documento de una solicitud anterior al
//               abrir este proceso. Sigue en `completed` (Listo), con la nota
//               «Tomamos este documento de tu solicitud anterior (dd/mm/aaaa).
//               Si cambió, puedes reemplazarlo» y el chip «Reemplazar»; se
//               puede abrir (el document-component ya reemplaza pasos listos).
//
// Antes «Por verificar» era inProcess (pasos que el usuario NI HA EMPEZADO) con
// el texto «Estamos verificando tu información»: de ahí la confusión.
//
// Orden: `order` del flujo (Number en el modelo); empates o sin número, en el
// orden en que llegan. Siguiente paso: el primer rechazado que se pueda abrir;
// si no hay, el primer pendiente habilitado (ese lleva «Te toca»).
//
// Pasos opcionales (`opcional: true`, configurados por flujo en Omnicanal): se
// pintan igual que los demás pero con el chip «Opcional», nunca son el
// siguiente paso (no llevan «Te toca») y no cuentan en `total` / `listos` ni en
// el avance de su sección: el colaborador termina con los obligatorios. Los
// que no están listos van en `opcionalesPendientes` para sugerirlos cuando ya
// no le toca nada. Sin la bandera (backend viejo) todo es obligatorio.
import {
  faArrowUpRightFromSquare,
  faBuildingColumns,
  faFileInvoiceDollar,
  faFileLines,
  faFileSignature,
  faHandHoldingDollar,
  faIdCard,
  faLock,
  faPenToSquare,
  faUser,
  faUserCheck,
  faVideo,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import {IFlow, IFlowByStages, IFlowSection, IFlowStatus} from "../../../../../data/interfaces/iflow.interface";
import {ESTADOS_LX, EstadoLx} from "../../../../../shared/ui/liga-ui";
import {
  archivosCargados,
  archivosRechazados,
  cantidadArchivos,
  minimoArchivos,
  pasoArchivosCompleto,
  textoAvanceArchivos,
  tieneRangoArchivos
} from "../../data/constants/documento-archivos.constant";

export type GrupoPaso = 'completed' | 'inValidation' | 'rejected' | 'unfinished' | 'inProcess'

// Orden en que se concatenan los grupos; solo decide los empates de `order`.
const GRUPOS: GrupoPaso[] = ['completed', 'inValidation', 'rejected', 'unfinished', 'inProcess']

export const ICONOS_TIPO_PASO: Record<string, IconDefinition> = {
  DOCUMENT: faFileLines,
  FORM: faPenToSquare,
  DRUO: faBuildingColumns,
  INCODE: faIdCard,
  VIDEO: faVideo,
  WEETRUST: faFileSignature,
  OFFER: faHandHoldingDollar,
  MANUAL: faUserCheck
}

// Cualquier otro tipo abre app-external-component (liga externa).
export const iconoDeTipo = (tipo: string | null | undefined): IconDefinition =>
  ICONOS_TIPO_PASO[tipo ?? ''] ?? faArrowUpRightFromSquare

export interface IPasoLista {
  id: string
  etapa: IFlow
  grupo: GrupoPaso
  estado: EstadoLx
  // Paso opcional del flujo: chip «Opcional», no cuenta en el avance.
  opcional: boolean
  // Posición 1..N en el orden del flujo.
  numero: number
  titulo: string
  subtitulo: string
  icono: IconDefinition
  // Solo en rechazados (comment del FlowData).
  motivo: string
  // «2 de 4 archivos» en pasos multiarchivo («3 de 4 necesarios (hasta 6)» o
  // «4 de 6 · completo» con rango); vacío en los de un archivo.
  archivos: string
  // Línea de ayuda del estado («Te avisamos cuando…», «Se habilita cuando…»).
  nota: string
  // Listo porque se tomó de una solicitud anterior (reutilizadoDe): lleva el
  // chip «Reemplazar» y se puede abrir para cambiarlo.
  reutilizado: boolean
  // Emite onClick al tocarlo (mismas reglas de siempre).
  abrible: boolean
  // Renglón con <button>: los que se abren y los bloqueados (aria-disabled,
  // enfocables para que se lea por qué). Listo y En revisión son informativos.
  esBoton: boolean
  etiquetaAria: string
}

export interface IResumenPasos {
  pasos: IPasoLista[]
  // Solo obligatorios: con ellos sale el avance y «Ya no te falta nada».
  total: number
  listos: number
  enRevision: number
  porCorregir: number
  porHacer: number
  siguiente: IPasoLista | null
  // Opcionales que aún se pueden subir (no listos y abribles), en el orden del flujo.
  opcionalesPendientes: IPasoLista[]
}

export const RESUMEN_VACIO: IResumenPasos = {
  pasos: [], total: 0, listos: 0, enRevision: 0, porCorregir: 0, porHacer: 0, siguiente: null, opcionalesPendientes: []
}

const esOpcional = (etapa: IFlow): boolean => etapa.opcional === true

const numeroDeOrden = (valor: unknown): number => {
  const n = Number(valor)
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
}

// «A», «A» y «B», «A», «B» y 2 más.
export const unirTitulos = (titulos: string[]): string => {
  const citados = titulos.map(t => `«${t}»`)
  if (citados.length <= 1) return citados[0] ?? ''
  if (citados.length === 2) return `${citados[0]} y ${citados[1]}`
  return `${citados[0]}, ${citados[1]} y ${citados.length - 2} más`
}

// «2 de 6 archivos»; con rango, «3 de 4 necesarios (hasta 6)» o «4 de 6 · completo».
const textoArchivos = (etapa: IFlow): string => {
  const total = cantidadArchivos(etapa)
  if (total <= 1) return ''
  return textoAvanceArchivos(archivosCargados(etapa), minimoArchivos(etapa), total, pasoArchivosCompleto(etapa))
}

// Archivos rechazados de un paso multiarchivo (cada índice cuenta una vez).
const rechazadosDe = (etapa: IFlow): number => cantidadArchivos(etapa) > 1 ? archivosRechazados(etapa) : 0

// Paso con rango («de 4 a 6») que ya pasó con el mínimo: se puede volver a abrir
// para subir los opcionales que faltan (el backend acepta subidas al paso ya
// VALIDATED; cada una reemplaza su índice).
const quedanOpcionales = (etapa: IFlow): boolean =>
  tieneRangoArchivos(etapa) && archivosCargados(etapa) < cantidadArchivos(etapa)

// dd/mm/aaaa de una fecha (Date o texto ISO); '' si no es fecha. Una fecha «sin
// hora» (medianoche UTC, como la fecha del documento que manda el backend o un
// «2026-09-15») se lee en UTC para que en México no se vea el día anterior.
export const fechaCorta = (valor: Date | string | null | undefined): string => {
  if (valor === null || valor === undefined || valor === '') return ''
  const d = valor instanceof Date ? valor : new Date(valor)
  if (Number.isNaN(d.getTime())) return ''
  const sinHora = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0
  const [dia, mes, anio] = sinHora
    ? [d.getUTCDate(), d.getUTCMonth() + 1, d.getUTCFullYear()]
    : [d.getDate(), d.getMonth() + 1, d.getFullYear()]
  return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${anio}`
}

// Documento tomado de una solicitud anterior: solo cuenta en un paso listo.
const esReutilizado = (etapa: IFlow, grupo: GrupoPaso): boolean =>
  grupo === 'completed' && !!etapa.reutilizadoDe && !!etapa.reutilizadoDe.copiadoEn

// «Tomamos este documento de tu solicitud anterior (dd/mm/aaaa). Si cambió, puedes reemplazarlo.»
export const notaReutilizado = (etapa: IFlow): string => {
  const r = etapa.reutilizadoDe
  const fecha = fechaCorta(r?.solicitudFecha ?? r?.documentoFecha ?? r?.copiadoEn)
  return `Tomamos este documento de tu solicitud anterior${fecha ? ` (${fecha})` : ''}. Si cambió, puedes reemplazarlo.`
}

interface IPasoCrudo {
  etapa: IFlow
  grupo: GrupoPaso
  llegada: number
}

const notaBloqueado = (etapa: IFlow, crudos: IPasoCrudo[]): string => {
  const ordenes = (etapa.dependsOn ?? []).map(o => String(o))
  const dependencias = crudos.filter(c => c.grupo !== 'completed' && ordenes.includes(String(c.etapa.order)))
  const deTi = dependencias.filter(c => c.grupo !== 'inValidation').map(c => tituloDe(c.etapa))
  if (deTi.length) return `Se habilita cuando termines ${unirTitulos(deTi)}.`
  const enRevision = dependencias.map(c => tituloDe(c.etapa))
  if (enRevision.length) return `Se habilita cuando revisemos ${unirTitulos(enRevision)}.`
  return 'Se habilita cuando revisemos tus pasos anteriores.'
}

const tituloDe = (etapa: IFlow): string => (etapa.properties?.title ?? '').trim() || 'Paso de tu solicitud'

export const armarPasos = (data: IFlowByStages | null | undefined): IResumenPasos => {
  const stages = data?.stages
  if (!stages) return RESUMEN_VACIO

  const crudos: IPasoCrudo[] = []
  for (const grupo of GRUPOS) {
    for (const etapa of stages[grupo] ?? []) {
      if (etapa) crudos.push({etapa, grupo, llegada: crudos.length})
    }
  }
  crudos.sort((a, b) => (numeroDeOrden(a.etapa.order) - numeroDeOrden(b.etapa.order)) || (a.llegada - b.llegada))

  // Quién sigue: primero lo que hay que corregir, luego lo pendiente habilitado.
  // Solo entre obligatorios: un opcional nunca lleva «Te toca».
  const obligatorios = crudos.filter(c => !esOpcional(c.etapa))
  const siguienteCrudo =
    obligatorios.find(c => c.grupo === 'rejected') ??
    obligatorios.find(c => (c.grupo === 'unfinished' || c.grupo === 'inProcess') && !c.etapa.disabled) ??
    null

  const total = obligatorios.length
  const pasos: IPasoLista[] = crudos.map((c, i) => {
    const {etapa, grupo} = c
    const opcional = esOpcional(etapa)
    const pendiente = grupo === 'unfinished' || grupo === 'inProcess'
    let estado: EstadoLx
    if (grupo === 'completed') estado = 'listo'
    else if (grupo === 'inValidation') estado = 'revision'
    else if (grupo === 'rejected') estado = 'corrige'
    else if (etapa.disabled) estado = 'bloqueado'
    else if (c === siguienteCrudo) estado = 'activo'
    else estado = 'pendiente'

    const motivo = grupo === 'rejected' ? (etapa.comment ?? '').trim() : ''
    const rechazados = rechazadosDe(etapa)
    // Listo con el mínimo de un rango y opcionales por subir: se puede abrir.
    const conOpcionales = grupo === 'completed' && !etapa.disabled && quedanOpcionales(etapa)
    // Listo porque se tomó de una solicitud anterior: se puede abrir para reemplazarlo.
    const reutilizado = esReutilizado(etapa, grupo)
    let nota = ''
    if (estado === 'revision') nota = 'Te avisamos cuando terminemos de revisarlo.'
    else if (estado === 'bloqueado') nota = notaBloqueado(etapa, crudos)
    else if (rechazados > 0) nota = rechazados === 1 ? '1 archivo por corregir.' : `${rechazados} archivos por corregir.`
    else if (estado === 'corrige' && !motivo) nota = 'Revísalo y vuelve a enviarlo.'
    else if (grupo === 'unfinished') nota = 'Lo empezaste; te falta terminarlo.'
    else if (reutilizado) nota = notaReutilizado(etapa)
    else if (conOpcionales) nota = 'Si quieres, sube los opcionales que faltan.'
    // Paso opcional pendiente: se puede omitir; no detiene la solicitud.
    else if (opcional && pendiente) nota = 'Opcional: si quieres, súbelo; no detiene tu solicitud.'

    const abrible = grupo === 'rejected' || (pendiente && !etapa.disabled) || conOpcionales || (reutilizado && !etapa.disabled)
    const titulo = tituloDe(etapa)
    const archivos = textoArchivos(etapa)
    const partesAria = [titulo, ESTADOS_LX[estado].descripcion]
    if (opcional) partesAria.push('Opcional')
    if (motivo) partesAria.push(`Motivo: ${motivo}`)
    if (archivos) partesAria.push(archivos)
    if (nota) partesAria.push(nota)
    if (reutilizado && abrible) partesAria.push('Reemplazar')

    return {
      id: String(etapa.id ?? etapa.properties?._id ?? i),
      etapa,
      grupo,
      estado,
      opcional,
      numero: i + 1,
      titulo,
      subtitulo: (etapa.properties?.subtitle ?? '').trim(),
      icono: estado === 'bloqueado' ? faLock : iconoDeTipo(etapa.properties?.type),
      motivo,
      archivos,
      nota,
      reutilizado,
      abrible,
      esBoton: abrible || estado === 'bloqueado',
      etiquetaAria: partesAria.join('. ')
    }
  })

  // Los conteos son de obligatorios: los opcionales no mueven el avance.
  const pasosObligatorios = pasos.filter(p => !p.opcional)
  const contar = (estado: EstadoLx) => pasosObligatorios.filter(p => p.estado === estado).length
  return {
    pasos,
    total,
    listos: contar('listo'),
    enRevision: contar('revision'),
    porCorregir: contar('corrige'),
    porHacer: pasosObligatorios.filter(p => p.estado === 'activo' || p.estado === 'pendiente' || p.estado === 'bloqueado').length,
    siguiente: siguienteCrudo ? pasos[crudos.indexOf(siguienteCrudo)] : null,
    opcionalesPendientes: pasos.filter(p => p.opcional && p.estado !== 'listo' && p.abrible)
  }
}

// Avance real para el encabezado: la lista manda (lo que el usuario ve); si
// aún no llega, el conteo de POST /flow/status.
export interface IAvance {
  listos: number
  total: number
  porcentaje: number
}

export const calcularAvance = (resumen: IResumenPasos, estado: IFlowStatus | null | undefined): IAvance => {
  let listos = resumen.listos
  let total = resumen.total
  if (!total && estado) {
    listos = Math.max(0, Math.floor(Number(estado.stagesCompleted) || 0))
    total = Math.max(0, Math.floor(Number(estado.totalStages) || 0))
  }
  listos = Math.min(listos, total)
  const porcentaje = total > 0 ? Math.round((listos / total) * 100) : 0
  return {listos, total, porcentaje}
}

// ---------- Secciones plegables ----------
// getFlow (pulpi-v4-app-backend, seccionesFlujo) manda en `data.sections` las
// secciones que tienen pasos, ya en orden de despliegue, y en cada paso su
// `sectionKey` resuelto: el que configuró el flujo o el automático por tipo
// (INE → Tu identidad, nómina → Tu capacidad de pago…). Aquí solo se reparten
// los renglones que ya armó armarPasos: dentro de cada sección siguen en el
// orden del flujo, y el siguiente paso, los estados y lo que abre cada renglón
// no cambian.
//
// Con 2 o más secciones con pasos la lista se pinta como acordeón; con 0 o 1
// (o un backend que aún no manda `sections`) queda la lista de siempre.
//
//   chip de la sección   cuándo
//   -------------------  -----------------------------------------------
//   Listo                todos sus pasos están listos
//   Corrige              alguno está rechazado
//   Te toca              contiene el siguiente paso
//   En revisión          lo que falta solo está en revisión
//   Pendiente            cualquier otro caso (pendientes o bloqueados)
//
// Abierta al pintar: la que contiene el siguiente paso (si no hay, la primera con
// algo por corregir); las demás cerradas.

export const ICONOS_SECCION: Record<string, IconDefinition> = {
  IDENTIDAD: faIdCard,
  CAPACIDAD: faFileInvoiceDollar,
  DATOS: faUser,
  CUENTA: faBuildingColumns,
  CREDITO: faHandHoldingDollar,
  DOCUMENTO: faFileLines,
  FIRMA: faFileSignature,
  VIDEO: faVideo,
  FORMULARIO: faPenToSquare
}

// Clave fuera del catálogo o vacía → ícono de documento.
export const iconoDeSeccion = (clave: string | null | undefined): IconDefinition =>
  ICONOS_SECCION[clave ?? ''] ?? faFileLines

// Pasos sin sección o con una clave que no llegó: al final, igual que el backend.
export const SECCION_OTROS: IFlowSection = {key: 'OTROS', name: 'Otros pasos', description: '', icon: 'DOCUMENTO', order: 0}

export interface ISeccionLista {
  clave: string
  nombre: string
  descripcion: string
  icono: IconDefinition
  pasos: IPasoLista[]
  total: number
  listos: number
  // «2 de 3 listos».
  avance: string
  // Chip de la sección (ver la tabla de arriba).
  estado: EstadoLx
  // Abierta al pintar la lista.
  abierta: boolean
}

const claveDe = (valor: unknown): string => String(valor ?? '').trim()

export const agruparPorSeccion = (
  pasos: IPasoLista[],
  sections: IFlowSection[] | null | undefined,
  siguiente: IPasoLista | null = null
): ISeccionLista[] => {
  if (!Array.isArray(sections) || !sections.length || !pasos.length) return []

  const grupos = new Map<string, {seccion: IFlowSection, pasos: IPasoLista[]}>()
  for (const seccion of sections) {
    const clave = claveDe(seccion?.key)
    if (clave && !grupos.has(clave)) grupos.set(clave, {seccion, pasos: []})
  }
  if (!grupos.size) return []

  for (const paso of pasos) {
    let grupo = grupos.get(claveDe(paso.etapa?.sectionKey))
    if (!grupo) {
      grupo = grupos.get(SECCION_OTROS.key)
      if (!grupo) {
        grupo = {seccion: SECCION_OTROS, pasos: []}
        grupos.set(SECCION_OTROS.key, grupo)
      }
    }
    grupo.pasos.push(paso)
  }

  const conPasos = Array.from(grupos.entries()).filter(([, g]) => g.pasos.length)
  if (conPasos.length < 2) return []

  // Sin `siguiente` explícito, el que lleva «Te toca».
  const esSiguiente = (p: IPasoLista) => siguiente ? p === siguiente : p.estado === 'activo'

  const secciones: ISeccionLista[] = conPasos.map(([clave, {seccion, pasos: suyos}]) => {
    // El avance y el chip de la sección son de sus obligatorios: una sección
    // con solo opcionales pendientes (o solo opcionales) queda Listo.
    const obligatorios = suyos.filter(p => !p.opcional)
    const total = obligatorios.length
    const listos = obligatorios.filter(p => p.estado === 'listo').length
    let estado: EstadoLx
    if (listos === total) estado = 'listo'
    else if (obligatorios.some(p => p.estado === 'corrige')) estado = 'corrige'
    else if (obligatorios.some(esSiguiente)) estado = 'activo'
    else if (obligatorios.every(p => p.estado === 'listo' || p.estado === 'revision')) estado = 'revision'
    else estado = 'pendiente'

    return {
      clave,
      nombre: (seccion.name ?? '').trim() || SECCION_OTROS.name,
      descripcion: (seccion.description ?? '').trim(),
      icono: iconoDeSeccion(seccion.icon),
      pasos: suyos,
      total,
      listos,
      // Sin obligatorios (solo opcionales) no hay «0 de 0»: se dice que es opcional.
      avance: total ? `${listos} de ${total} ${total === 1 ? 'listo' : 'listos'}` : 'Opcional',
      estado,
      abierta: false
    }
  })

  const abierta = secciones.find(s => s.pasos.some(esSiguiente)) ?? secciones.find(s => s.estado === 'corrige')
  if (abierta) abierta.abierta = true
  return secciones
}
